import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { VERTICALS } from "@postman/core/verticals";
import { AttachmentList } from "@/components/attachments";
import { AnswerFields, AnswerValue, type Answers } from "@/components/form";
import { Markdown } from "@/components/markdown";
import { Accent, Deadline, StatePill, Stepper } from "@/components/pipeline";
import { Button, Callout, Card, Empty, PageTitle, Pill } from "@/components/ui";
import {
  applicationQuery,
  applicationsQuery,
  clearDraft,
  readDraft,
  useSubmitRound,
  useWithdraw,
  writeDraft,
  type DetailRound,
} from "@/lib/applications";

export const Route = createFileRoute("/_auth/_onboarded/apply/$vertical")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(applicationQuery(params.vertical)),
  component: Apply,
});

const BY_SLUG = new Map(VERTICALS.map((v) => [v.slug, v]));

function Apply() {
  const { vertical } = Route.useParams();
  const { data } = useSuspenseQuery(applicationQuery(vertical));
  const list = useSuspenseQuery(applicationsQuery);
  const meta = BY_SLUG.get(vertical as never);

  const app = data.application;
  const ended = app?.state === "rejected" || app?.state === "withdrawn";
  const current = data.rounds.find((r) => r.current) ?? null;
  const history = data.rounds.filter((r) => r.reached && r.id !== current?.id);

  const stepperRounds = list.data.items.find((i) => i.slug === vertical)?.application?.rounds ?? [];

  return (
    <Accent slug={vertical} className="flex flex-col gap-12">
      <div className="flex flex-col gap-6">
        <Link to="/apply" className="mono text-ink-3 hover:text-ink">
          ← All verticals
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-5">
          <PageTitle
            eyebrow={data.cycle.name}
            title={data.vertical.name}
            lede={meta?.blurb}
          />
          {app ? <StatePill state={app.state} /> : <Pill tone="live">Open</Pill>}
        </div>
      </div>

      {stepperRounds.length > 0 && app ? (
        <section className="flex flex-col gap-5">
          <h2 className="mono text-ink-3">Where you are</h2>
          <Stepper rounds={stepperRounds} state={app.state} />
        </section>
      ) : null}

      {app?.state === "selected" ? (
        <Callout>
          You are in. Your coordinator will be in touch about what happens next.
        </Callout>
      ) : null}
      {app?.state === "rejected" ? (
        <Callout>
          This one did not work out. It says nothing about the other six, and
          nothing about applying again next cycle.
        </Callout>
      ) : null}
      {app?.state === "withdrawn" ? (
        <Callout>You withdrew from this vertical.</Callout>
      ) : null}

      {current && !ended ? (
        <RoundForm key={current.id} vertical={vertical} round={current} />
      ) : null}

      {!current && !ended && app ? (
        <Empty>
          Nothing to do right now — everything you have sent is with the
          coordinators.
        </Empty>
      ) : null}

      {history.length > 0 ? (
        <section className="flex flex-col gap-5">
          <h2 className="mono text-ink-3">What you have sent</h2>
          <div className="flex flex-col gap-5">
            {history.map((round) => (
              <PastRound key={round.id} round={round} />
            ))}
          </div>
        </section>
      ) : null}

      {app && app.state !== "withdrawn" && app.state !== "selected" ? (
        <Withdraw vertical={vertical} name={data.vertical.name} />
      ) : null}
    </Accent>
  );
}

/**
 * The round in front of you.
 *
 * Answers are held in component state and mirrored into localStorage on every
 * keystroke, so a closed tab does not cost a paragraph. The draft is cleared
 * the moment the server has the real thing — see the note on `readDraft`.
 */
function RoundForm({ vertical, round }: { vertical: string; round: DetailRound }) {
  const submit = useSubmitRound(vertical);
  const questions = useMemo(() => round.form?.questions ?? [], [round.form]);

  const [answers, setAnswers] = useState<Answers>(
    () => readDraft(round.id) ?? (round.submission?.answers as Answers) ?? {},
  );
  const [sent, setSent] = useState(false);

  useEffect(() => {
    writeDraft(round.id, answers);
  }, [round.id, answers]);

  const closed = !!round.closesAt && new Date(round.closesAt).getTime() < Date.now();

  async function send() {
    setSent(false);
    await submit.mutateAsync({ roundId: round.id, answers });
    clearDraft(round.id);
    setSent(true);
  }

  return (
    <Card className="card-scrim flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="display text-[26px]">{round.name}</h2>
          <Deadline at={round.closesAt} />
        </div>
        {round.instructions ? (
          <Markdown source={round.instructions} className="max-w-[62ch]" />
        ) : null}
        {round.attachments.length > 0 ? (
          <div className="flex flex-col gap-2 pt-2">
            <p className="mono text-ink-3">Files for this round</p>
            <AttachmentList items={round.attachments} />
          </div>
        ) : null}
      </div>

      {closed ? (
        <Callout>
          The deadline has passed. You can still send this — it will be marked
          late, and your coordinator decides what that is worth.
        </Callout>
      ) : null}

      {round.submission ? (
        <Callout>
          Sent{" "}
          {new Date(round.submission.submittedAt!).toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            hour: "numeric",
            minute: "2-digit",
          })}
          {round.submission.late ? " — marked late." : "."} You can change your
          answers until a decision is made.
        </Callout>
      ) : null}

      {questions.length > 0 ? (
        <AnswerFields
          questions={questions}
          answers={answers}
          onChange={setAnswers}
          disabled={submit.isPending}
        />
      ) : (
        <p className="text-[15px] text-ink-2">
          This round has no form — send it to say you are ready, and your
          coordinator will take it from there.
        </p>
      )}

      {submit.error ? <Callout tone="error">{(submit.error as Error).message}</Callout> : null}
      {sent && !submit.error ? <Callout>Saved.</Callout> : null}

      <div className="flex items-center gap-4">
        <Button onClick={send} disabled={submit.isPending}>
          {submit.isPending ? "Sending" : round.submission ? "Update answers" : "Send it"}
        </Button>
        {/* Not "profile and resume" any more — the resume is optional, and
            promising a coordinator a document you never uploaded reads as a
            missing attachment rather than as a choice. */}
        <p className="mono text-ink-3">Your profile goes with this.</p>
      </div>
    </Card>
  );
}

function PastRound({ round }: { round: DetailRound }) {
  const questions = round.form?.questions ?? [];
  const labels = new Map(questions.map((q) => [q.id, q.label]));
  const answers = (round.submission?.answers ?? {}) as Answers;
  // Read back in the order the form asked, not the order jsonb stored: a
  // jsonb object sorts its keys by length, so `Object.entries` hands back your
  // own answers shuffled.
  const keys = [
    ...questions.map((q) => q.id).filter((id) => id in answers),
    ...Object.keys(answers).filter((id) => !labels.has(id)),
  ];

  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="display text-[20px]">{round.name}</h3>
        {round.result ? (
          <Pill tone={round.result.outcome === "rejected" ? "quiet" : "good"}>
            {round.result.outcome === "advanced"
              ? "Cleared"
              : round.result.outcome === "selected"
                ? "Selected"
                : "Closed here"}
          </Pill>
        ) : round.submission ? (
          <Pill tone="live">With us</Pill>
        ) : null}
      </div>

      {round.instructions ? (
        <Markdown source={round.instructions} className="max-w-[62ch]" />
      ) : null}

      {round.attachments.length > 0 ? (
        <AttachmentList items={round.attachments} />
      ) : null}

      {round.result?.note ? (
        <p className="border-l-2 border-[color:var(--accent)] py-1 pl-4 text-[15px] leading-relaxed text-ink-2">
          {round.result.note}
        </p>
      ) : null}

      {keys.length > 0 ? (
        <dl className="flex flex-col gap-5">
          {keys.map((id) => (
            <div key={id} className="flex flex-col gap-1.5">
              <dt className="mono text-ink-3">{labels.get(id) ?? "Answer"}</dt>
              <dd>
                <AnswerValue value={answers[id]} />
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-[14px] text-ink-3">Nothing to show — this round had no form.</p>
      )}
    </Card>
  );
}

/**
 * Withdrawing is deliberately two clicks and no dialog.
 *
 * A `confirm()` would block the page and, in this app, the browser modal is
 * the one thing that can wedge an automated session — but the real reason is
 * that an inline confirmation says what will happen in the same place you are
 * looking, instead of a grey box asking "Are you sure?".
 */
function Withdraw({ vertical, name }: { vertical: string; name: string }) {
  const withdraw = useWithdraw(vertical);
  const [asking, setAsking] = useState(false);

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-8">
      <h2 className="mono text-ink-3">Changed your mind?</h2>
      {asking ? (
        <div className="flex flex-col gap-4">
          <p className="max-w-[56ch] text-[15px] leading-relaxed text-ink-2">
            Withdrawing from {name} tells the coordinators to stop considering
            you. Your answers stay on record and you cannot re-apply to this
            vertical in this cycle.
          </p>
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={() => withdraw.mutate()}
              disabled={withdraw.isPending}
            >
              {withdraw.isPending ? "Withdrawing" : `Withdraw from ${name}`}
            </Button>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        <button
          className="mono self-start text-ink-3 underline underline-offset-4 hover:text-ink"
          onClick={() => setAsking(true)}
        >
          Withdraw this application
        </button>
      )}
      {withdraw.error ? (
        <Callout tone="error">{(withdraw.error as Error).message}</Callout>
      ) : null}
    </section>
  );
}
